import { type DragEvent, type PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventListener } from 'usehooks-ts';
import cn from '@/utils/merge-class-names';

interface Props {
  className?: string;
  onFileDrop: (data: DataTransfer) => void;
}

enum DragState {
  False = 0, // 未拖拽
  Outside = 1, // 外部
  Inside = 2, // 内部
}

export default function AttachmentDndZone({ onFileDrop, children, className }: PropsWithChildren<Props>) {
  const { t } = useTranslation();
  const [dragState, setDragState] = useState<DragState>(DragState.False);

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!e.dataTransfer?.types.includes('Files')) return;
    setDragState(DragState.Inside);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragState(DragState.Inside);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    const hasData = e.dataTransfer?.types.includes('Files');
    if (hasData) {
      e.preventDefault();
      if (!document.contains(e.relatedTarget as Node)) {
        setDragState(DragState.False);
      } else {
        setDragState(DragState.Outside);
      }
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (e.dataTransfer.types.includes('Files')) {
      onFileDrop(e.dataTransfer);
    }
  };

  // 只有不在提示浮窗时，才会触发 修改为 outside
  useEventListener('dragover', (e) => {
    if (dragState === DragState.Outside) return;
    const hasData = e.dataTransfer?.types.includes('Files');
    if (hasData) {
      e.preventDefault();
      if (dragState === DragState.False) setDragState(DragState.Outside);
    }
  });

  // 离开浏览器时，重置状态
  useEventListener('dragleave', (e) => {
    const hasData = e.dataTransfer?.types.includes('Files');
    if (hasData) {
      e.preventDefault();
      if (!document.contains(e.relatedTarget as Node)) {
        setDragState(DragState.False);
      }
    }
  });

  // 当鼠标释放时，重置状态
  useEventListener('drop', (e) => {
    const hasData = e.dataTransfer?.types.includes('Files');
    if (hasData) {
      setDragState(DragState.False);
    }
  });

  return (
    <div
      className={cn('relative', className)}
      onDragEnter={handleDragEnter}>
      {children}

      {dragState !== DragState.False && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'inset-0 absolute bg-neutral-950/50 backdrop-blur-sm z-40 text-neutral-0 flex items-center justify-center transition-colors',
            dragState === DragState.Inside && 'bg-primary/50',
          )}>
          {dragState === DragState.Outside ? t('upload.dnd.drag-here-hint') : t('upload.dnd.drop-here-hint')}
        </div>
      )}
    </div>
  );
}
